import { useEffect, useState } from "react";
import { Shield, Plus, X, Save, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";

// Admin → Settings → Proposal View Builder: IP restrictions for who may open a
// shared (public) proposal link.
export default function ProposalViewBuilderTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [ips, setIps] = useState<string[]>([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    axiosInstance
      .get("/proposal-view-settings")
      .then((r) => { setEnabled(!!r.data.ipRestrictionEnabled); setIps(r.data.allowedIps || []); })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const addIp = () => {
    const v = draft.trim();
    if (!v) return;
    if (ips.includes(v)) { toast("Already added", { icon: "ℹ️" }); return; }
    setIps((p) => [...p, v]);
    setDraft("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.put("/proposal-view-settings", { ipRestrictionEnabled: enabled, allowedIps: ips });
      toast.success("Proposal view settings saved");
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="bg-white p-6 rounded-xl border border-outline-variant shadow-sm max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <Shield size={18} className="text-primary" />
        <h3 className="text-lg font-bold text-on-surface">IP Restrictions</h3>
      </div>
      <p className="text-[13px] text-on-surface-variant mb-5">
        Restrict who can open a shared public proposal link by IP address. Internal proposal management
        (list, edit, analytics) is never affected. Leave the list empty to allow everyone.
      </p>

      <label className="flex items-center gap-3 mb-5 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 accent-primary" />
        <span className="text-[14px] font-semibold text-on-surface">Enable IP restriction for public proposal views</span>
      </label>

      <div className={enabled ? "" : "opacity-50 pointer-events-none"}>
        <label className="block text-[12px] font-medium text-on-surface-variant mb-1.5">Allowed IPs / CIDR ranges</label>
        <div className="flex gap-2 mb-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addIp(); } }}
            placeholder="e.g. 203.0.113.9 or 203.0.113.0/24"
            className="flex-1 px-3 py-2 rounded-lg bg-background border border-outline-variant text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button onClick={addIp} className="inline-flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-[#004b9e]">
            <Plus size={15} /> Add
          </button>
        </div>
        {ips.length === 0 ? (
          <p className="text-[12px] text-outline mb-4">No IPs added — all viewers allowed.</p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-4">
            {ips.map((ip) => (
              <span key={ip} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-container-high text-[12px] font-semibold text-on-surface">
                {ip}
                <button onClick={() => setIps((p) => p.filter((x) => x !== ip))} className="text-outline hover:text-error"><X size={13} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-900 disabled:opacity-50">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Settings
      </button>
    </div>
  );
}
